import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useBatchMergeJobStatusQuery, useBatchMergeMutation, useFindAutoMergeCandidatesMutation } from '../store';
import type {
  AutoMergeCandidate,
  BatchMergeJobStatusResponse,
  FindAutoMergeCandidatesResponse,
} from '../types';

type AutoMergeStatus = 'idle' | 'scanning' | 'reviewing' | 'merging' | 'complete' | 'error';

const POLL_INTERVAL_MS = 10_000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes

/** Job statuses that indicate the merge is still in progress. */
const POLLING_STATUSES = new Set(['pending', 'processing']);

interface AutoMergeState {
  status: AutoMergeStatus;
  candidates: AutoMergeCandidate[];
  deselectedGroups: Set<string>;
  mergeResult: BatchMergeJobStatusResponse | null;
  skippedGroups: number;
  error: string | null;
  jobId: string | null;
}

const INITIAL_STATE: AutoMergeState = {
  status: 'idle',
  candidates: [],
  deselectedGroups: new Set(),
  mergeResult: null,
  skippedGroups: 0,
  error: null,
  jobId: null,
};

export function useAutoMerge(organizationId: string | null | undefined) {
  const [state, setState] = useState<AutoMergeState>(INITIAL_STATE);
  const { enqueueSnackbar } = useSnackbar();
  const { patientDeduplication } = useEventTracking();

  const [findCandidates] = useFindAutoMergeCandidatesMutation();
  const [batchMerge] = useBatchMergeMutation();

  const startTimeRef = useRef(0);

  const approvedCount = state.candidates.length - state.deselectedGroups.size;

  // ---------------------------------------------------------------------------
  // Job status polling — manual setInterval + refetch (same pattern as
  // usePatientImportSession). RTK Query's pollingInterval has a render-delay
  // where skip/pollingInterval hasn't updated yet but the poll fires, causing
  // runaway requests even after completion or navigation.
  // ---------------------------------------------------------------------------
  const { data: jobStatus, refetch: refetchJobStatus } = useBatchMergeJobStatusQuery(
    { jobId: state.jobId! },
    { skip: !state.jobId, refetchOnFocus: false },
  );

  const shouldPoll = !!state.jobId && (!jobStatus || POLLING_STATUSES.has(jobStatus.status));

  const pollStartRef = useRef(Date.now());
  useEffect(() => {
    if (!shouldPoll) return;

    pollStartRef.current = Date.now();

    const interval = setInterval(() => {
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        clearInterval(interval);
        return;
      }
      if (document.hidden) return;

      refetchJobStatus();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [shouldPoll, refetchJobStatus]);

  // React to job status changes
  useEffect(() => {
    if (!jobStatus || state.status !== 'merging') return;

    if (jobStatus.status === 'completed') {
      const mergeDurationMs = Math.round(performance.now() - startTimeRef.current);

      patientDeduplication.batchMergeCompleted({
        organization_id: organizationId ?? '',
        total_groups: jobStatus.total_groups ?? 0,
        approved_groups: approvedCount,
        successful: jobStatus.successful ?? 0,
        failed: jobStatus.failed ?? 0,
        merge_duration_ms: mergeDurationMs,
      });

      setState(prev => ({ ...prev, status: 'complete', mergeResult: jobStatus, jobId: null }));
    } else if (jobStatus.status === 'failed') {
      const message = jobStatus.error_message ?? 'Batch merge job failed';

      patientDeduplication.batchMergeFailed(message, {
        organization_id: organizationId ?? '',
        total_groups: approvedCount,
      });

      setState(prev => ({ ...prev, status: 'error', error: message, jobId: null }));
      enqueueSnackbar('Batch merge failed', { variant: 'error' });
    }
  }, [jobStatus, state.status, organizationId, approvedCount, enqueueSnackbar, patientDeduplication]);

  const startAutoMerge = useCallback(async () => {
    if (!organizationId) {
      enqueueSnackbar('Organization not found', { variant: 'error' });
      return;
    }

    setState(prev => ({ ...prev, status: 'scanning', error: null }));
    startTimeRef.current = performance.now();

    try {
      const result: FindAutoMergeCandidatesResponse = await findCandidates({
        findAutoMergeCandidatesRequest: {
          organization_id: organizationId,
          confidence_threshold: 50,
        },
      }).unwrap();

      const scanDurationMs = Math.round(performance.now() - startTimeRef.current);

      patientDeduplication.autoMergeCandidatesLoaded({
        organization_id: organizationId,
        total_candidates: result.auto_merge_candidates.length,
        skipped_groups: result.skipped_groups,
        scan_duration_ms: scanDurationMs,
      });

      setState(prev => ({
        ...prev,
        status: result.auto_merge_candidates.length > 0 ? 'reviewing' : 'idle',
        candidates: result.auto_merge_candidates,
        deselectedGroups: new Set(),
        skippedGroups: result.skipped_groups,
      }));

      if (result.auto_merge_candidates.length === 0) {
        enqueueSnackbar(
          `No groups eligible for auto-merge. ${result.skipped_groups} groups need manual review.`,
          { variant: 'info' },
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, status: 'error', error: message }));
      enqueueSnackbar('Failed to find auto-merge candidates', { variant: 'error' });
    }
  }, [organizationId, findCandidates, enqueueSnackbar, patientDeduplication]);

  const toggleGroupSelection = useCallback((groupId: string) => {
    setState(prev => {
      const next = new Set(prev.deselectedGroups);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return { ...prev, deselectedGroups: next };
    });
  }, []);

  const selectAllGroups = useCallback(() => {
    setState(prev => ({ ...prev, deselectedGroups: new Set() }));
  }, []);

  const deselectAllGroups = useCallback(() => {
    setState(prev => ({
      ...prev,
      deselectedGroups: new Set(prev.candidates.map(c => c.group_id)),
    }));
  }, []);

  const confirmBatchMerge = useCallback(async () => {
    if (!organizationId) return;

    const approvedCandidates = state.candidates.filter(
      c => !state.deselectedGroups.has(c.group_id),
    );

    if (approvedCandidates.length === 0) {
      enqueueSnackbar('No groups selected for merge', { variant: 'warning' });
      return;
    }

    setState(prev => ({ ...prev, status: 'merging', error: null }));
    startTimeRef.current = performance.now();

    try {
      const accepted = await batchMerge({
        batchMergeRequest: {
          organization_id: organizationId,
          groups: approvedCandidates.map(candidate => ({
            group_id: candidate.group_id,
            primary_patient_id: candidate.suggested_primary_id,
            patient_ids_to_merge: candidate.patients
              .filter(p => p.patient_id !== candidate.suggested_primary_id)
              .map(p => p.patient_id),
            demographic_overrides:
              Object.keys(candidate.demographic_overrides).length > 0
                ? candidate.demographic_overrides
                : undefined,
          })),
        },
      }).unwrap();

      // Batch merge is async — store the job ID and poll for completion
      setState(prev => ({ ...prev, jobId: accepted.job_id }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      patientDeduplication.batchMergeFailed(message, {
        organization_id: organizationId,
        total_groups: approvedCandidates.length,
      });

      setState(prev => ({ ...prev, status: 'error', error: message, jobId: null }));
      enqueueSnackbar('Batch merge failed', { variant: 'error' });
    }
  }, [organizationId, state.candidates, state.deselectedGroups, batchMerge, enqueueSnackbar, patientDeduplication]);

  const cancelAutoMerge = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    state,
    approvedCount,
    jobProgress: jobStatus ? { processed: jobStatus.processed_groups, total: jobStatus.total_groups } : null,
    startAutoMerge,
    toggleGroupSelection,
    selectAllGroups,
    deselectAllGroups,
    confirmBatchMerge,
    cancelAutoMerge,
  };
}
